import {
    DADOS_CARTEIRA_ENTRADA,
    SIMULADOR_HISTORICO,
    SIMULADOR_SIMULACAO,
    SIMULADOR_SUBORDINACAO_SIMULADO,
    SIMULADOR_SUBORDINACAO
} from "src/utils/api-routes"
import {formatarValor, formatDecimalCaseReplaceDot, formatToFloat} from "src/utils/format-decimal"
import {ServiceRequest} from "src/utils/service-request"
import {applyRegex, reduzirString} from "src/utils/strings"
import * as XLSX from 'xlsx-js-style'
import {formatDateString} from "src/utils/format-date"

const HEADER_STYLE = {
    fill: {fgColor: {rgb: "222851"}},
    font: {color: {rgb: "FFFFFF", sz: 14, bold: true}},
    alignment: {
        wrapText: true,
        horizontal: "center",
        vertical: "center"
    },
    border: {
        top: {style: 'thin', color: {argb: '000000'}},
        bottom: {style: 'thin', color: {argb: '000000'}},
        left: {style: 'thin', color: {argb: '000000'}},
        right: {style: 'thin', color: {argb: '000000'}}
    }
}

class SimuladorService {

    constructor() {
        this.request = new ServiceRequest()
    }

    getHistorico() {
        return this.request.get(SIMULADOR_HISTORICO)
    }

    // busca um lote selecionado de carteira ou concessão
    getHistoricoSimulado(IDSubordinacaoLote) {
        return this.request.get(`${SIMULADOR_SUBORDINACAO_SIMULADO}/lote/${IDSubordinacaoLote}`)
    }

    createWorksheet(data, headers) {
        const worksheetData = data.map(item => {
            const rowData = {}
            headers.forEach(header => {
                let value = item[header.key] ?? ''

                if (header.isDate) {
                    value = formatDateString(value)
                }

                if (header.format) {
                    value = formatDecimalCaseReplaceDot(value)
                }

                rowData[header.label] = value
            })
            return rowData
        })

        const worksheet = XLSX.utils.json_to_sheet(worksheetData)

        worksheet['!cols'] = headers.map(h => ({
            wch: Math.max(h.label.length, ...data.map(d => (d[h.key] ?? '').toString().length)) + 2
        }))

        headers.forEach((_, colIndex) => {
            const cell = XLSX.utils.encode_cell({r: 0, c: colIndex})
            worksheet[cell].s = HEADER_STYLE
        })

        return worksheet
    }

    exportExcel(NomeSimulacao, results, subordinacaoEntrada) {
        const workbook = XLSX.utils.book_new()

        // Aba RESULTADOS
        const headersResultado = [
            {label: "Nome da Cota", key: "NomeCota", format: false},
            {label: "PL da Cota", key: "PLCota", format: true},
            {label: "Indexador", key: "IndexadorCota", format: false},
            {label: "Taxa Accrual", key: "TaxaAccrualCota", format: true},
            {label: "Duration Cota", key: "DurationCota", format: true},
            {label: "Subordinação", key: "SubordinacaoContabilCota", format: true},
            {label: "Rating", key: "RatingCota", format: false},
            {label: "Taxa MTM", key: "TaxaMTMCota", format: true},
            {label: "Spread", key: "SpreadMTMCota", format: true},
            {label: "PD", key: "PDCota", format: true},
            {label: "LGD", key: "LGDCota", format: true},
            {label: "EAD", key: "EADCota", format: true},
            {label: "Perda Esperada", key: "PerdaEsperadaCota", format: true},
            {label: "Credit VaR", key: "CreditVaRCota", format: true},
            {label: "Credit Tail VaR", key: "CreditTailVarCota", format: true},
            {label: "Data Referência", key: "DataReferenciaOriginal", isDate: true},
            {label: "Data Processamento", key: "DataReferencia", isDate: true}
        ]

        const worksheetResultado = this.createWorksheet(
            results,
            headersResultado
        )

        const nomePlanilhaResultado = applyRegex(reduzirString(NomeSimulacao, 31))
        XLSX.utils.book_append_sheet(workbook, worksheetResultado, nomePlanilhaResultado)


        // Aba PARAMETROS DE ENTRADA
        if (subordinacaoEntrada) {
            const entradaArray = [subordinacaoEntrada]
            const headersEntrada = [
                {label: "Tipo Simulação", key: "TipoSimulacao", format: false},
                {label: "Tipo PDD", key: "TipoPDD", format: false},
                {label: "Nome Carteira", key: "NomeCarteira", format: false},
                {label: "Quantidade Operações", key: "QuantidadeOperacoes", format: true},
                {label: "PL Total", key: "PLTotal", format: true},
                {label: "Percentil", key: "Percentil", format: true},
                {label: "Caixa", key: "Caixa", format: true},
                {label: "Multiplicador", key: "Multiplicador", format: true},
                {label: "Data Referência", key: "DataReferencia", isDate: true},
                {label: "Data Processamento", key: "DataProcessamento", isDate: true},
                {label: "Volume Financeiro", key: "VolumeFinanceiro", format: true},
                {label: "PDD Observada", key: "PDDObservada", format: true},
                {label: "PDD Sugerida", key: "PDDSugerida", format: true},
                {label: "PL Cotas", key: "PLCotas", format: true},
            ]

            const worksheetEntrada = this.createWorksheet(
                entradaArray,
                headersEntrada
            )

            XLSX.utils.book_append_sheet(
                workbook,
                worksheetEntrada,
                "Simulação_Entrada"
            )
        }

        XLSX.writeFile(workbook, `${nomePlanilhaResultado}.xlsx`)
    }

    // ABA SIMULAÇÃO
    getSelectSimulacao() {
        return this.request.get(`${SIMULADOR_SIMULACAO}`)
    }

    loadLoteSelecionado(loteUUID) {
        return this.request.get(`${SIMULADOR_SIMULACAO}/${loteUUID}`)
    }

    getDadosCarteiraEntrada(uuidCarteiraEntrada) {
        return this.request.get(`${DADOS_CARTEIRA_ENTRADA}/${uuidCarteiraEntrada}`)
    }

    // IMPORTANTE: data_referencia agora recebe como valor o campo Data Processamento
    postSimulacao(
        idCarteira,
        nome_carteira,
        data_referencia_original_formatada,
        data_referencia,
        caixa,
        percentil,
        plTotal,
        multiplicador,
        tipo_simulacao,
        quantidade_operacoes,
        pddSelecionada,
        typePDDObservada,
        subordinacoes
    ) {
        let body = {
            uuid: idCarteira,
            data_referencia_original_formatada,
            data_referencia: data_referencia,
            nome_carteira: nome_carteira,
            percentil: percentil,
            plTotal: plTotal,
            caixa: formatToFloat(caixa),
            tipo_simulacao: tipo_simulacao,
            quantidade_operacoes: quantidade_operacoes,
            multiplicador: multiplicador,
            pddSelecionada: pddSelecionada,
            typePDDObservada: typePDDObservada,
            dados_carteira_entrada_simulacao: subordinacoes.map((entrada) => ({
                Caixa: formatToFloat(caixa),
                NomeCota: entrada.NomeCota,
                PLCota: formatarValor(entrada.PLCota),
                IndexadorCota: entrada.IndexadorCota,
                TaxaAccrualCota: formatarValor(entrada.TaxaAccrualCota),
                DurationCota: parseInt(entrada.DurationCota),
                OrdemPL: entrada.OrdemPL,
            })),
        }

        return this.request.post(`${SIMULADOR_SUBORDINACAO}`, body)
    }

    saveSimulation(idCarteira, nome_carteira, data_referencia, data_processamento, caixa, percentil, plCotas, plTotal, multiplicador, volumeFinanceiro, pddObservada, pddSugerida, tipoSimulacao, qtdOperacoes, typePDDObservada, subordinacoes) {
        const body = {
            uuid: idCarteira,
            nome_carteira,
            data_referencia_original_formatada: data_referencia,
            data_processamento,
            tipoSimulacao,
            tipoPDD: typePDDObservada,
            volumeFinanceiro: formatToFloat(volumeFinanceiro),
            pddObservada: formatToFloat(pddObservada),
            pddSugerida: formatToFloat(pddSugerida),
            qtdOperacoes: qtdOperacoes,
            plCotas: formatToFloat(plCotas),
            multiplicador: formatToFloat(multiplicador),
            percentil,
            caixa: formatToFloat(caixa),
            plTotal: formatToFloat(plTotal),
            dados_carteira_entrada_simulacao: subordinacoes
        }
        return this.request.post(`${SIMULADOR_SUBORDINACAO}/registro`, body)
    }

}

export default SimuladorService
